import React, { useEffect, useMemo, useRef } from "react";
import { HiOutlineLocationMarker, HiOutlineShoppingBag } from "react-icons/hi";
import {
  PRODUCT_ROLE,
  PRODUCT_ROLE_LABEL,
} from "./productRoleConstants";
import {
  categoryHasMultipleRequiredCards,
  getProductSelectionLimits,
  isAutoIncludedRequiredCompanion,
  isGroupedRequiredSupply,
  isMultipleProductSelection,
  normalizeProductSelections,
  productCategoryIdentity,
  productSelectionCategoryError,
  requiredSupplyGroups,
  requiredSupplyOptionFor,
} from "./checkoutContract";
import { patientProductName } from "./patientProductName";
import { US_STATE_NAMES } from "../../constants/usStates";
import "./treatmentProductSelection.css";

const values = (value) => Array.isArray(value) ? value : value ? [value] : [];
const optionId = (item) => String(
  item?.option_id || item?.value || item?.id || item?.product_id || item || "",
);
const treatmentTypeId = (item) => String(
  item?.treatment_type_id || item?.treatment_type_key || "",
);
const productRole = (item) => item?.product_role || PRODUCT_ROLE.primaryChoice;
const authoredChoiceGroup = (item) => String(item?.choice_group || "").trim();
const authoredSupplyGroup = (item) => {
  const group = authoredChoiceGroup(item);
  return group.startsWith("supply-") ? group : "";
};
const catalogProductId = (item) => String(
  item?.product_source_id
  || item?.source_product_id
  || item?.product_id
  || item?.productId
  || optionId(item),
);
const choiceGroup = (item) => String(
  authoredChoiceGroup(item) || optionId(item),
);
// Same stable-identity rule as checkoutContract/the server: never a name.
const productIdentity = (item) => String(
  item?.clinical_group_key
  || item?.product_source_id
  || item?.source_product_id
  || item?.product_id
  || item?.productId
  || "",
).trim().toLowerCase();
const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const refillCount = (choice) => {
  const count = Number(choice?.refills);
  return Number.isInteger(count) && count > 0 ? count : 0;
};
const supplyLabel = (choice, days) => {
  const refills = refillCount(choice);
  return `${days}-day supply${refills ? ` + ${refills} refill${refills === 1 ? "" : "s"}` : ""}`;
};
const pharmacyName = (choice) => String(
  choice?.pharmacy_name || choice?.pharmacy?.store_name || "Pharmacy not configured",
).trim();
const configuredPharmacyNames = (choices = []) => [...new Set(choices
  .map((choice) => choice?.pharmacy_name || choice?.pharmacy?.store_name)
  .filter(Boolean)
  .map((value) => String(value).trim()))];
const pharmacyTone = (name, choices = []) => {
  const palette = ["blue", "violet", "emerald", "amber", "rose", "cyan"];
  const pharmacies = configuredPharmacyNames(choices).sort((a, b) => a.localeCompare(b));
  const index = pharmacies.indexOf(name);
  return palette[(index < 0 ? 0 : index) % palette.length];
};
const hasMultiplePharmacySources = (choices = []) => configuredPharmacyNames(choices).length > 1;

export const resolveProductImageUrl = (product) => {
  const rawImage = product?.product_image || product?.image || "";
  if (typeof rawImage !== "string" || !rawImage) return "";
  if (/^https?:\/\//i.test(rawImage) || rawImage.startsWith("data:")) return rawImage;
  if (typeof window === "undefined") return rawImage;
  const clientApiEndpoint = window.localStorage.getItem("welliemd_client_api_endpoint") || "";
  if (!clientApiEndpoint) return rawImage;
  try {
    return new URL(rawImage, new URL(clientApiEndpoint).origin).toString();
  } catch (_error) {
    return rawImage;
  }
};

const durationPrice = (choice, days) => {
  if (!choice) return null;
  const configured = choice.duration_prices?.[String(days)];
  if (configured != null) return Number(configured);
  const base = Number(choice.unit_price || choice.price || choice.final_price);
  const authoredDays = Number(
    choice.rx_days_supply || choice.days_supply || choice.default_supply_duration || 0,
  );
  if (authoredDays === Number(days) && authoredDays > 0) return base;
  return null;
};

const normalizeSelection = (choice, days) => ({
  ...choice,
  option_id: optionId(choice),
  value: optionId(choice),
  days_supply: Number(
    days || choice.rx_days_supply || choice.days_supply || choice.default_supply_duration || 0,
  ) || undefined,
  // R3 sells one unit per option; linked supplies carry their own quantity.
  quantity: 1,
});

const selectionKey = (choice, customProgram = false) => {
  const role = productRole(choice);
  const scope = customProgram
    ? String(choice?.program_id || treatmentTypeId(choice))
    : treatmentTypeId(choice);
  if (isPatientOrderable(choice)) {
    return `${scope}:choice:${choiceGroup(choice)}`;
  }
  return `${scope}:${role}:${optionId(choice)}`;
};

export const isPatientOrderable = (choice) => ![
  PRODUCT_ROLE.clinicianOnly,
  PRODUCT_ROLE.informational,
].includes(productRole(choice));

export const buildPrograms = (choices, { customProgram = false } = {}) => {
  const programs = new Map();
  choices.forEach((choice) => {
    const key = customProgram
      ? String(choice.program_id || treatmentTypeId(choice))
      : treatmentTypeId(choice);
    if (!key) {
      throw new Error(
        "Treatment option is missing authoritative Treatment Type identity.",
      );
    }
    if (!programs.has(key)) {
      programs.set(key, {
        id: key,
        name: customProgram
          ? choice.program_name || choice.treatment_type_name
            || choice.__sourceQuestionText || "Treatment"
          : choice.program_name || choice.treatment_type_name
            || choice.__sourceQuestionText || "Treatment",
        choices: [],
      });
    }
    programs.get(key).choices.push(choice);
  });
  return [...programs.values()].map((program) => ({
    ...program,
    choiceGroups: Object.values(
      program.choices
        .filter(
          (choice) =>
            isPatientOrderable(choice)
            && productRole(choice) === PRODUCT_ROLE.primaryChoice,
        )
        .reduce((groups, choice) => {
          const key = choiceGroup(choice);
          groups[key] = [...(groups[key] || []), choice];
          return groups;
        }, {}),
    ),
    required: program.choices.filter((choice) => (
      !authoredSupplyGroup(choice)
      && isAutoIncludedRequiredCompanion(choice, program.choices)
    )),
    optional: program.choices.filter((choice) => {
      if (productRole(choice) !== PRODUCT_ROLE.optionalAddon) return false;
      // A mixed supply group has a required member and an independently
      // selectable optional sibling, even when both reference one catalog
      // identity.
      if (authoredSupplyGroup(choice)) return true;
      const identity = productIdentity(choice);
      // An empty identity is "unknown", never a match. Two options with no
      // stable catalog reference would otherwise compare equal and hide the
      // add-on from the patient.
      if (!identity) return true;
      return !program.choices.some((candidate) => candidate !== choice
        && productIdentity(candidate) === identity
        && isPatientOrderable(candidate));
    }),
    displayOnly: program.choices.filter((choice) => !isPatientOrderable(choice)),
  }));
};

const authoredDefault = (group) => {
  const configured = group.filter(
    (choice) => choice.is_default === true
      || choice.default_selected === true,
  );
  if (configured.length === 1) return configured[0];
  return group[0] || null;
};

function GroupedRequiredSupplyContents({
  group,
  multipleSelected,
  onSelectSupply,
  serviceState,
  choices = [],
}) {
  const requiredChoices = group.requiredChoices || [];
  const pharmacyNames = configuredPharmacyNames(requiredChoices);
  const hasMultipleSources = pharmacyNames.length > 1;
  const anchor = requiredChoices.find(
    (choice) => productRole(choice) === PRODUCT_ROLE.requiredCompanion,
  ) || requiredChoices[0];
  const isRequiredAlternative = Boolean(
    anchor && categoryHasMultipleRequiredCards(anchor, choices),
  );
  const selectedRequired = multipleSelected.find((item) => (
    requiredChoices.some((choice) => optionId(choice) === item.option_id)
  ));
  const activeRequired = requiredChoices.find(
    (choice) => optionId(choice) === selectedRequired?.option_id,
  ) || requiredChoices[0];
  const showAutoIncluded = !hasMultipleSources && !isRequiredAlternative;
  const includedChrome = !hasMultipleSources && !isRequiredAlternative;

  // WT-174: one inset box for every product. A product served by several
  // pharmacies differs only in its rows ("Fulfilled by …" per pharmacy), its
  // label and the routing note, never in the card chrome.
  return (
    <>
        <div className={`treatment-product-card__section treatment-product-card__product${includedChrome ? " treatment-product-card__included" : ""}${isRequiredAlternative ? " treatment-product-card--selectable" : ""}${isRequiredAlternative && selectedRequired ? " is-selected" : ""}`}>
        <ProductSummary
          choice={activeRequired}
          badge={hasMultipleSources || isRequiredAlternative
            ? null
            : PRODUCT_ROLE_LABEL[PRODUCT_ROLE.requiredCompanion]}
          patientFacing
        />
        <div className="treatment-product-card__supply-label">
          {hasMultipleSources ? "Choose a pharmacy" : "Choose your supply"}
        </div>
        <DurationChoices
          choice={activeRequired}
          choices={requiredChoices}
          selected={selectedRequired || null}
          onSelect={(choice) => onSelectSupply(requiredChoices, choice)}
        />
        {showAutoIncluded && <span>Included automatically</span>}
        {hasMultipleSources && (
          <p className="treatment-product-card__routing-note">
            {serviceState
              ? `${pharmacyNames.length} pharmacies serve ${US_STATE_NAMES[serviceState] || serviceState}. Select one to continue.`
              : "Several pharmacies can fulfill this treatment. Select one to continue."}
          </p>
        )}
      </div>
      {(group.selectableGroups || []).map((selectableGroup) => {
        const selectedSelectable = multipleSelected.find((item) => (
          selectableGroup.choices.some((choice) => optionId(choice) === item.option_id)
        ));
        const activeSelectable = selectableGroup.choices.find(
          (choice) => optionId(choice) === selectedSelectable?.option_id,
        ) || selectableGroup.choices[0];
        const isOptional = productRole(activeSelectable) === PRODUCT_ROLE.optionalAddon;
        return (
          <div
            className={`treatment-product-card__section treatment-product-card__product treatment-product-card--selectable${selectedSelectable ? " is-selected" : ""}`}
            key={selectableGroup.key}
          >
            <ProductSummary
              choice={activeSelectable}
              badge={isOptional ? PRODUCT_ROLE_LABEL[PRODUCT_ROLE.optionalAddon] : null}
              patientFacing
            />
            <div className="treatment-product-card__supply-label">
              Choose your supply
            </div>
            <DurationChoices
              choice={activeSelectable}
              choices={selectableGroup.choices}
              selected={selectedSelectable || null}
              onSelect={(choice) => onSelectSupply(selectableGroup.choices, choice)}
            />
          </div>
        );
      })}
    </>
  );
}

function DurationChoices({
  choice,
  choices = [choice],
  selected,
  onSelect,
  readOnly = false,
}) {
  const currentOptionId = selected === null ? "" : optionId(selected);
  const currentDays = selected === null
    ? 0
    : Number(
      selected?.days_supply || choice.rx_days_supply || choice.days_supply || choice.default_supply_duration || 0,
    );
  const seenOffers = new Set();
  const sourceChoices = choices || [choice];
  const hasMultipleSources = hasMultiplePharmacySources(sourceChoices);
  const pharmacyChoices = sourceChoices;
  const offers = sourceChoices.flatMap((item) => {
    const exact = Number(item.rx_days_supply || item.days_supply || item.default_supply_duration || 0);
    const configured = item.allowed_supply_durations || [];
    const daysList = exact > 0 ? [exact] : configured.filter((days) => durationPrice(item, Number(days)) != null);
    return daysList.map(Number).filter((days) => days > 0).map((days) => ({
      item, days, refills: Math.max(0, Number(item.refills || 0)),
    }));
  }).filter(({ item, days, refills }) => {
    // Every exact Product remains selectable. Two pharmacies may intentionally
    // offer the same clinical presentation, fill duration, and refill count.
    const key = `${catalogProductId(item)}:${days}:${refills}`;
    if (seenOffers.has(key)) return false;
    seenOffers.add(key);
    return true;
  }).sort((a, b) => a.days - b.days || a.refills - b.refills);
  const sourceGroups = [...offers.reduce((groups, offer) => {
    const source = offer.item;
    const key = String(
      source?.pharmacy_id
      || source?.pharmacy?.id
      || source?.pharmacy_name
      || source?.pharmacy?.store_name
      || "default-source",
    ).trim().toLowerCase();
    const existing = groups.get(key);
    groups.set(key, existing
      ? { ...existing, offers: [...existing.offers, offer] }
      : { key, source, offers: [offer] });
    return groups;
  }, new Map()).values()];
  const sourceDisplayNames = new Set(sourceGroups.map((sourceGroup) => {
    const representative = sourceGroup.source;
    return patientProductName(representative)
      || representative.patient_label
      || representative.name
      || "Treatment option";
  }));
  const showSourceDisplayNames = sourceDisplayNames.size > 1;
  return (
    <div className={`treatment-product-card__durations${hasMultipleSources ? " treatment-product-card__durations--pharmacy-options" : ""}`}>
      {sourceGroups.map((sourceGroup) => {
        const selectedOffer = sourceGroup.offers.find(({ item, days }) => (
          currentOptionId
            ? Number(days) === currentDays && optionId(item) === currentOptionId
            : Number(days) === currentDays
        ));
        const representative = selectedOffer?.item || sourceGroup.source;
        const imageUrl = resolveProductImageUrl(representative);
        const displayName = patientProductName(representative)
          || representative.patient_label
          || representative.name
          || "Treatment option";
        const sourceName = pharmacyName(representative);
        const selectDefault = () => {
          if (readOnly) return;
          const offer = selectedOffer || sourceGroup.offers[0];
          if (offer) onSelect(offer.item, offer.days);
        };
        return (
          <div
            className={`treatment-product-card__source-card${selectedOffer ? " is-selected" : ""}`}
            role="radio"
            aria-checked={Boolean(selectedOffer)}
            tabIndex={readOnly ? -1 : 0}
            onClick={selectDefault}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                selectDefault();
              }
            }}
            key={sourceGroup.key}
          >
            <span className="treatment-product-card__duration-radio" aria-hidden="true">
              <i />
            </span>
            <span className="treatment-product-card__source-image">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  loading="lazy"
                  onError={(event) => { event.currentTarget.hidden = true; }}
                />
              ) : <span aria-hidden="true">Rx</span>}
            </span>
            <span className="treatment-product-card__choice-copy">
              {showSourceDisplayNames && (
                <strong className="treatment-product-card__choice-name">{displayName}</strong>
              )}
              {representative.pharmacy_name || representative.pharmacy?.store_name ? (
                <span className={`treatment-product-card__pharmacy-badge treatment-product-card__pharmacy-badge--${pharmacyTone(sourceName, pharmacyChoices)}`}>
                  <HiOutlineShoppingBag aria-hidden="true" /> Fulfilled by {sourceName}
                </span>
              ) : null}
              <span className="treatment-product-card__supply-choices" aria-label="Choose your supply">
                {sourceGroup.offers.map(({ item: durationChoice, days, refills }) => {
                  const active = currentOptionId
                    ? Number(days) === currentDays && optionId(durationChoice) === currentOptionId
                    : Number(days) === currentDays;
                  const price = durationPrice(durationChoice, days);
                  if (price == null) return null;
                  return (
                    <button
                      type="button"
                      className={`treatment-product-card__supply-choice${active ? " is-selected" : ""}`}
                      aria-pressed={active}
                      disabled={readOnly}
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelect(durationChoice, days);
                      }}
                      key={`${optionId(durationChoice)}:${days}:${refills}`}
                    >
                      <span>{supplyLabel(durationChoice, days)}</span>
                      <strong>{money(price)}</strong>
                      <small>
                        {refills > 0
                          ? `${days}-day fill · ${refills} refill${refills === 1 ? "" : "s"}`
                          : `One-time charge for ${days} days`}
                      </small>
                    </button>
                  );
                }).filter(Boolean)}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

const patientProductDescription = (choice) => {
  const regimen = String(
    choice.regimen
    || choice.titration_category
    || choice.regimen_name
    || choice.regimenName
    || "Standard regimen",
  ).trim();
  return regimen || "Standard regimen";
};

function ProductSummary({ choice, badge, patientFacing = false, showImage = true }) {
  const supportingLabel = choice.patient_label
    && choice.patient_label !== choice.name
    ? choice.patient_label
    : "";
  const title = patientFacing
    ? patientProductName(choice) || "Treatment option"
    : choice.name || choice.label || choice.patient_label || "Treatment option";
  const description = patientFacing
    ? patientProductDescription(choice)
    : supportingLabel || choice.description ||
      `${choice.rx_days_supply || choice.default_supply_duration || "Configured"}-day standard regimen`;
  const imageUrl = showImage ? resolveProductImageUrl(choice) : "";
  return (
    <div className="treatment-product-card__summary">
      {imageUrl && (
        <img
          className="treatment-product-card__image"
          src={imageUrl}
          alt=""
          loading="lazy"
          onError={(event) => { event.currentTarget.hidden = true; }}
        />
      )}
      <div className="treatment-product-card__summary-copy">
        {badge && (
          <span className="treatment-product-card__role">{badge}</span>
        )}
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </div>
  );
}

function RequiredCompanion({ choice, selected, patientFacing = false }) {
  return (
    <div className="treatment-product-card__included">
      <ProductSummary
        choice={choice}
        badge={PRODUCT_ROLE_LABEL[PRODUCT_ROLE.requiredCompanion]}
        patientFacing={patientFacing}
      />
      <div className="treatment-product-card__supply-label">
        Included supply
      </div>
      <DurationChoices
        choice={choice}
        choices={[choice]}
        selected={selected || normalizeSelection(choice)}
        onSelect={() => {}}
        readOnly
      />
      <span>Included automatically</span>
    </div>
  );
}

export default function TreatmentProductSelection({
  question,
  value,
  onChange,
  error,
  customProgram = false,
  serviceState = "",
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const choices = useMemo(
    () => (question.answer_choices || []).filter(Boolean),
    [question.answer_choices],
  );
  const programs = useMemo(
    () => buildPrograms(choices, { customProgram }),
    [choices, customProgram],
  );
  const autoIncludedRequiredIds = useMemo(
    () => new Set(programs.flatMap((program) => program.required.map(optionId))),
    [programs],
  );
  const multipleSelection = isMultipleProductSelection(question);
  const selected = useMemo(() => {
    const unique = new Map();
    values(value).forEach((item) => unique.set(selectionKey(item, customProgram), item));
    return [...unique.values()];
  }, [customProgram, value]);
  const signature = selected
    .map((item) => `${selectionKey(item, customProgram)}:${optionId(item)}:${item.days_supply || ""}`)
    .sort()
    .join("|");

  const multipleSelected = useMemo(
    () => normalizeProductSelections(value, choices),
    [choices, value],
  );
  const categoryError = productSelectionCategoryError(question, value);
  const groupedRequiredSupplyGroups = useMemo(() => (
    requiredSupplyGroups(choices)
  ), [choices]);
  const multipleChoiceGroups = useMemo(() => {
    const groups = new Map();
    choices.forEach((choice) => {
      if (
        !isPatientOrderable(choice)
        || autoIncludedRequiredIds.has(optionId(choice))
      ) {
        return;
      }
      const requiredSupplyGroup = groupedRequiredSupplyGroups.find((group) => (
        group.includes(choice)
      ));
      const requiredSupplyAnchor = requiredSupplyGroup?.find(
        (member) => productRole(member) === PRODUCT_ROLE.requiredCompanion,
      ) || requiredSupplyGroup?.[0];
      const programScope = customProgram
        ? String(choice.program_id || treatmentTypeId(choice))
        : treatmentTypeId(choice);
      const key = requiredSupplyGroup
        ? `${String(requiredSupplyAnchor.program_id || requiredSupplyAnchor.treatment_type_id || requiredSupplyAnchor.treatment_type_key || "").trim()}:supply:${authoredSupplyGroup(requiredSupplyAnchor)}`
        : authoredSupplyGroup(choice)
        ? `${programScope}:supply:${authoredSupplyGroup(choice)}`
        : `${programScope}:product:${catalogProductId(choice)}`;
      const existing = groups.get(key);
      groups.set(key, existing
        ? { ...existing, choices: [...existing.choices, choice] }
        : { key, choices: [choice] });
    });
    return [...groups.values()].map((group) => {
      const requiredChoices = group.choices.filter(
        (choice) => productRole(choice) === PRODUCT_ROLE.requiredCompanion,
      );
      const selectableChoices = group.choices.filter(
        (choice) => productRole(choice) !== PRODUCT_ROLE.requiredCompanion
          && isPatientOrderable(choice),
      );
      const selectableGroups = Object.values(selectableChoices.reduce(
        (selectableByProduct, choice) => {
          const selectableKey = `${group.key}:selectable:${catalogProductId(choice)}`;
          selectableByProduct[selectableKey] = selectableByProduct[selectableKey] || {
            key: selectableKey,
            choices: [],
          };
          selectableByProduct[selectableKey].choices.push(choice);
          return selectableByProduct;
        },
        {},
      ));
      return {
        ...group,
        requiredChoices,
        selectableGroups,
      };
    });
  }, [autoIncludedRequiredIds, choices, customProgram, groupedRequiredSupplyGroups]);
  // WT-174: Required alternatives of one category share one program card, so
  // the one-product-per-category rule is visible. Every other group keeps its
  // own card.
  const multipleChoiceCards = useMemo(() => {
    const cards = new Map();
    multipleChoiceGroups.forEach((group) => {
      const anchor = (group.requiredChoices || [])[0];
      const category = anchor && isGroupedRequiredSupply(group.choices)
        && categoryHasMultipleRequiredCards(anchor, choices)
        ? productCategoryIdentity(anchor)
        : "";
      const key = category
        ? `${String(anchor.program_id || treatmentTypeId(anchor))}:category:${category}`
        : group.key;
      if (!cards.has(key)) cards.set(key, { key, groups: [] });
      cards.get(key).groups.push(group);
    });
    return [...cards.values()];
  }, [choices, multipleChoiceGroups]);
  const multipleProgramGroups = useMemo(() => {
    const groups = new Map();
    if (customProgram) {
      programs.forEach((program) => {
        groups.set(program.id, {
          id: program.id,
          name: program.name,
          groups: [],
          required: program.required,
        });
      });
    }
    multipleChoiceGroups.forEach((group) => {
      const firstChoice = group.choices[0] || {};
      const id = customProgram
        ? String(firstChoice.program_id || treatmentTypeId(firstChoice))
        : group.key;
      if (!groups.has(id)) {
        groups.set(id, {
          id,
          name: customProgram
            ? firstChoice.program_name || firstChoice.treatment_type_name
              || firstChoice.__sourceQuestionText || "Treatment"
            : question.program_name
              || question.treatment_type_name
              || firstChoice.program_name
              || firstChoice.treatment_type_name
              || question.question_text
              || "Product option",
          groups: [],
          required: customProgram
            ? programs.find((program) => program.id === id)?.required || []
            : [],
        });
      }
      groups.get(id).groups.push(group);
    });
    return [...groups.values()];
  }, [customProgram, multipleChoiceGroups, programs, question]);

  useEffect(() => {
    const defaults = [];
    programs.forEach((program) => {
      if (!multipleSelection) {
        program.choiceGroups.forEach((group) => {
          const defaultChoice = authoredDefault(group);
          if (
            defaultChoice
            && !selected.some(
              (item) => selectionKey(item, customProgram)
                === selectionKey(group[0], customProgram),
            )
          ) {
            defaults.push(normalizeSelection(defaultChoice));
          }
        });
      }
      program.required.forEach((choice) => {
        if (!selected.some((item) => selectionKey(item, customProgram)
          === selectionKey(choice, customProgram))) {
          defaults.push(normalizeSelection(choice));
        }
      });
    });
    if (multipleSelection) {
      groupedRequiredSupplyGroups.forEach((group) => {
        const anchor = group.find(
          (choice) => productRole(choice) === PRODUCT_ROLE.requiredCompanion,
        ) || group[0];
        // Several Required cards in one category are alternatives. Do not
        // preselect a duration on every card.
        if (anchor && categoryHasMultipleRequiredCards(anchor, choices)) return;
        // The authored order is the product editor's first duration. It is
        // selected automatically, while DurationChoices still allows a
        // patient to switch to another duration in the same group.
        const defaultChoice = requiredSupplyOptionFor(group, selected);
        if (!defaultChoice || group.some(
          (choice) => productRole(choice) === PRODUCT_ROLE.requiredCompanion
            && selected.some((item) => optionId(choice) === optionId(item)),
        )) return;
        defaults.push(normalizeProductSelections([defaultChoice], choices)[0]);
      });
    }
    if (defaults.length) onChangeRef.current([...selected, ...defaults]);
  }, [
    choices,
    customProgram,
    groupedRequiredSupplyGroups,
    multipleSelection,
    programs,
    signature,
  ]);
  const multipleLimits = getProductSelectionLimits(question);

  const countCardinalitySelections = (items) => items.filter((item) => {
    const choice = choices.find((candidate) => optionId(candidate) === optionId(item)) || item;
    return [PRODUCT_ROLE.primaryChoice, PRODUCT_ROLE.optionalAddon].includes(
      productRole(choice),
    );
  }).length;

  const replaceCategorySelection = (items, choice) => {
    const category = productCategoryIdentity(choice);
    if (!category) return items;
    return items.filter((item) => {
      const selectedChoice = choices.find(
        (candidate) => optionId(candidate) === optionId(item),
      ) || item;
      const sameCategory = productCategoryIdentity(selectedChoice) === category;
      if (!sameCategory) return true;
      if (productRole(choice) === PRODUCT_ROLE.requiredCompanion) {
        return productRole(selectedChoice) !== PRODUCT_ROLE.requiredCompanion;
      }
      if (![PRODUCT_ROLE.primaryChoice, PRODUCT_ROLE.optionalAddon].includes(
        productRole(selectedChoice),
      )) return true;
      return false;
    });
  };

  if (
    question.runtime_availability === "unavailable"
    && choices.length === 0
  ) {
    return (
      <section className="treatment-products">
        <div className="treatment-products__eyebrow">Treatment availability</div>
        <h2>Treatment options are temporarily unavailable</h2>
        <p className="treatment-products__intro" role="alert">
          Please review your previous answers or contact the care team before
          continuing.
        </p>
      </section>
    );
  }

  const selectMultipleSupply = (groupChoices, choice) => {
    const groupOptionIds = new Set(groupChoices.map(optionId));
    const remainingInCard = multipleSelected.filter(
      (item) => !groupOptionIds.has(item.option_id),
    );
    const remaining = replaceCategorySelection(remainingInCard, choice);
    const wasSelected = multipleSelected.some(
      (item) => item.option_id === optionId(choice),
    );
    if (wasSelected) {
      if (isGroupedRequiredSupply(groupChoices)) return;
      onChange(remaining);
      return;
    }
    if (
      productRole(choice) !== PRODUCT_ROLE.requiredCompanion
      && multipleLimits.maximum !== null
      && countCardinalitySelections(remaining) >= multipleLimits.maximum
    ) {
      return;
    }
    onChange([
      ...remaining,
      normalizeProductSelections([choice], choices)[0],
    ]);
  };

  const replaceSelection = (key, choice, days) => {
    onChange([
      ...selected.filter((item) => selectionKey(item, customProgram) !== key),
      normalizeSelection(choice, days),
    ]);
  };
  const toggleOptional = (choice) => {
    const exists = selected.some((item) => selectionKey(item, customProgram)
      === selectionKey(choice, customProgram));
    const withoutCurrent = selected.filter((item) => selectionKey(item, customProgram)
      !== selectionKey(choice, customProgram));
    if (exists) {
      onChange(withoutCurrent);
      return;
    }
    const remaining = replaceCategorySelection(withoutCurrent, choice);
    if (
      multipleLimits.maximum !== null
      && countCardinalitySelections(remaining) >= multipleLimits.maximum
    ) return;
    onChange([...remaining, normalizeSelection(choice)]);
  };

  const hasProductCategories = choices.some((choice) => (
    isPatientOrderable(choice) && productCategoryIdentity(choice)
  ));

  return (
    <section className="treatment-products">
      <h2>
        {programs.length === 1
          ? "Recommended treatment"
          : `${programs.length} treatments recommended`}
      </h2>
      <p className="treatment-products__intro">
        {hasProductCategories
          ? "Choose up to one product from each category and one supply option per card. Products from different categories can be selected together."
          : choices.some((choice) => choice.pharmacy_name || choice.pharmacy?.store_name)
          ? "Choose your preferred fulfillment option."
          : "Based on your screening, here is the treatment our doctors recommend. Pick a supply duration for each."}
      </p>
      {serviceState && choices.some((choice) => choice.pharmacy_name || choice.pharmacy?.store_name) && (
        <div className="treatment-products__state-badge">
          <HiOutlineLocationMarker aria-hidden="true" />
          Available in {US_STATE_NAMES[serviceState] || serviceState}
        </div>
      )}
      <div className="treatment-products__list">
        {multipleSelection ? (
          customProgram
            ? multipleProgramGroups.map((program) => {
              return (
                <article className="treatment-product-card" key={program.id}>
                  <div className="treatment-product-card__program">{program.name}</div>
                  {program.groups.map((group) => {
                  const groupedRequiredSupply = isGroupedRequiredSupply(group.choices);
                  if (groupedRequiredSupply) {
                    return (
                      <GroupedRequiredSupplyContents
                        key={group.key}
                        group={group}
                        multipleSelected={multipleSelected}
                        onSelectSupply={selectMultipleSupply}
                        serviceState={serviceState}
                        choices={choices}
                      />
                    );
                  }
                  const selectedItem = multipleSelected.find(
                    (item) => group.choices.some(
                      (choice) => optionId(choice) === item.option_id,
                    ),
                  );
                  const checked = Boolean(selectedItem);
                  const activeChoice = group.choices.find(
                    (choice) => optionId(choice) === selectedItem?.option_id,
                  ) || group.choices[0];
                  return (
                    <div
                      className={`treatment-product-card__section treatment-product-card__product${groupedRequiredSupply ? " treatment-product-card__included" : ""}${checked ? " is-selected" : ""}`}
                      key={group.key}
                    >
                      <ProductSummary
                        choice={activeChoice}
                        badge={groupedRequiredSupply
                          ? PRODUCT_ROLE_LABEL[PRODUCT_ROLE.requiredCompanion]
                          : null}
                        patientFacing
                      />
                      <div className="treatment-product-card__supply-label">
                        {groupedRequiredSupply ? "Included supply" : "Choose your supply"}
                      </div>
                      <DurationChoices
                        choice={activeChoice}
                        choices={group.choices}
                        selected={selectedItem || null}
                        onSelect={(choice) => selectMultipleSupply(group.choices, choice)}
                      />
                      {groupedRequiredSupply && <span>Included automatically</span>}
                    </div>
                  );
                  })}
                  {program.required.map((choice) => (
                    <RequiredCompanion
                      key={`required-${program.id}-${optionId(choice)}`}
                      choice={choice}
                      selected={multipleSelected.find(
                        (item) => optionId(item) === optionId(choice),
                      )}
                      patientFacing
                    />
                  ))}
                </article>
              );
            })
            : (
              <>
                {multipleChoiceCards.map((card) => {
                  const [group] = card.groups;
                  const groupedRequiredSupply = isGroupedRequiredSupply(group.choices);
                  if (groupedRequiredSupply) {
                    return (
                      <article className="treatment-product-card" key={card.key}>
                        <div className="treatment-product-card__program">
                          {question.program_name
                            || question.treatment_type_name
                            || group.choices[0].program_name
                            || group.choices[0].treatment_type_name
                            || question.question_text
                            || "Product option"}
                        </div>
                        {card.groups.map((cardGroup) => (
                          <GroupedRequiredSupplyContents
                            key={cardGroup.key}
                            group={cardGroup}
                            multipleSelected={multipleSelected}
                            onSelectSupply={selectMultipleSupply}
                            serviceState={serviceState}
                            choices={choices}
                          />
                        ))}
                      </article>
                    );
                  }
                  const selectedItem = multipleSelected.find(
                    (item) => group.choices.some(
                      (choice) => optionId(choice) === item.option_id,
                    ),
                  );
                  const checked = Boolean(selectedItem);
                  const activeChoice = group.choices.find(
                    (choice) => optionId(choice) === selectedItem?.option_id,
                  ) || group.choices[0];
                  return (
                    <article
                      className={`treatment-product-card${groupedRequiredSupply ? "" : " treatment-product-card--selectable"}${checked && !groupedRequiredSupply ? " is-selected" : ""}`}
                      key={group.key}
                    >
                      <div className="treatment-product-card__program">
                        {question.program_name
                          || question.treatment_type_name
                          || activeChoice.program_name
                          || activeChoice.treatment_type_name
                          || question.question_text
                          || "Product option"}
                      </div>
                      <div className={groupedRequiredSupply ? "treatment-product-card__included" : ""}>
                        <ProductSummary
                          choice={activeChoice}
                          badge={groupedRequiredSupply
                            ? PRODUCT_ROLE_LABEL[PRODUCT_ROLE.requiredCompanion]
                            : null}
                          patientFacing
                        />
                        <div className="treatment-product-card__supply-label">
                          {groupedRequiredSupply ? "Included supply" : "Choose your supply"}
                        </div>
                        <DurationChoices
                          choice={activeChoice}
                          choices={group.choices}
                          selected={selectedItem || null}
                          onSelect={(choice) => selectMultipleSupply(group.choices, choice)}
                        />
                        {groupedRequiredSupply && <span>Included automatically</span>}
                      </div>
                    </article>
                  );
                })}
                {programs.flatMap((program) => program.required.map((choice) => (
                  <article className="treatment-product-card" key={`required-${program.id}-${optionId(choice)}`}>
                    <div className="treatment-product-card__program">{program.name}</div>
                    <RequiredCompanion
                      choice={choice}
                      selected={multipleSelected.find(
                        (item) => optionId(item) === optionId(choice),
                      )}
                      patientFacing
                    />
                  </article>
                )))}
              </>
            )
        ) : programs.map((program) => (
          <article className="treatment-product-card" key={program.id}>
            <div className="treatment-product-card__program">{program.name}</div>
            {program.choiceGroups.map((group) => {
              const isAuthoredSupply = Boolean(authoredSupplyGroup(group[0]));
              const key = selectionKey(group[0], customProgram);
              const current = selected.find(
                (item) => selectionKey(item, customProgram) === key,
              );
              const active = group.find(
                (choice) => optionId(choice) === optionId(current),
              ) || null;
              return (
                <div
                  className={customProgram
                    ? "treatment-product-card__section treatment-product-card__product"
                    : "treatment-product-card__section"}
                  key={key}
                >
                  {active || isAuthoredSupply ? (
                    <ProductSummary choice={active || group[0]} patientFacing={isAuthoredSupply} />
                  ) : (
                    <div className="treatment-product-card__summary">
                      <h3>Choose one treatment option</h3>
                    </div>
                  )}
                  {!isAuthoredSupply && group.length > 1 && new Set(group.map(productIdentity)).size > 1 && (
                    <div
                      className="treatment-product-card__options"
                      role="radiogroup"
                      aria-label="Choose product"
                    >
                      {group.map((choice) => (
                        <button
                          type="button"
                          role="radio"
                          aria-checked={optionId(choice) === optionId(active)}
                          className={
                            optionId(choice) === optionId(active)
                              ? "is-selected"
                              : ""
                          }
                          onClick={() => replaceSelection(key, choice)}
                          key={optionId(choice)}
                        >
                          {choice.name || choice.label || "Treatment option"}
                        </button>
                      ))}
                    </div>
                  )}
                  {(active || isAuthoredSupply) && (
                    <>
                      <div className="treatment-product-card__supply-label">
                        Choose your supply
                      </div>
                      <DurationChoices
                        choice={active || group[0]}
                        choices={isAuthoredSupply ? group : group.filter(
                          (candidate) => productIdentity(candidate) === productIdentity(active),
                        )}
                        selected={current}
                        onSelect={(choice, days) => replaceSelection(key, choice, days)}
                      />
                    </>
                  )}
                </div>
              );
            })}
            {program.required.map((choice) => (
              <div className="treatment-product-card__included" key={optionId(choice)}>
                <ProductSummary
                  choice={choice}
                  badge={PRODUCT_ROLE_LABEL[PRODUCT_ROLE.requiredCompanion]}
                />
                <div className="treatment-product-card__supply-label">
                  Included supply
                </div>
                <DurationChoices
                  choice={choice}
                  choices={[choice]}
                  selected={selected.find(
                    (item) => optionId(item) === optionId(choice),
                  ) || normalizeSelection(choice)}
                  onSelect={() => {}}
                  readOnly
                />
                <span>Included automatically</span>
              </div>
            ))}
            {program.optional.map((choice) => {
              const checked = selected.some(
                (item) => optionId(item) === optionId(choice),
              );
              return (
                <button
                  type="button"
                  className={`treatment-product-card__optional${checked ? " is-selected" : ""}`}
                  aria-pressed={checked}
                  onClick={() => toggleOptional(choice)}
                  key={optionId(choice)}
                >
                  <ProductSummary
                    choice={choice}
                    badge={PRODUCT_ROLE_LABEL[PRODUCT_ROLE.optionalAddon]}
                  />
                  <span>{checked ? "Added" : "Add"}</span>
                </button>
              );
            })}
            {program.displayOnly.map((choice) => (
              <div className="treatment-product-card__information" key={optionId(choice)}>
                <ProductSummary
                  choice={choice}
                  badge={PRODUCT_ROLE_LABEL[productRole(choice)]}
                />
              </div>
            ))}
          </article>
        ))}
      </div>
      <div className="treatment-products__review-note">
        A licensed doctor will review your answers before any prescription is issued.
      </div>
      {(error || categoryError) && (
        <div className="treatment-products__error" role="alert">
          {error || categoryError}
        </div>
      )}
    </section>
  );
}
